import React, { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import '@mantine/core/styles.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import branch from 'branch-sdk';
import ErrorFallback from './molecules/ErrorFallback';
import FireFoxModal from './molecules/FireFoxModal';
import PWAInstallBanner from './molecules/PWAInstallBanner';
import SafariModal from './molecules/SafariModal';
import useInstallPromptStore from './stores/eventListenerStore';
import { useLocalStorageStore } from './stores/localStorageStore';
import { initDataDog, logErrorToDataDog } from './utils/datadogUtil';
import { getBrowser } from './utils/generalUtils';
import {
  initMixpanel,
  initPostHog,
  MixpanelEvent,
  sendPostHogEvent,
} from './utils/mixpanelUtil';

import './utils/webViewBridge';
// import { migrateOldLocalStorageToStore } from './migration';
import { getQueryParams } from './utils/deeplink';

function Main() {
  const [isPwaInstall, setPwaInstall] = useState(false);
  const [showSafariModal, setShowSafariModal] = useState(false);
  const [showFirefoxModal, setShowFirefoxModal] = useState(false);

  const deferredPrompt = useInstallPromptStore((state) => state.deferredPrompt);
  const clearDeferredPrompt = useInstallPromptStore(
    (state) => state.clearDeferredPrompt
  );
  const setDeferredPrompt = useInstallPromptStore(
    (state) => state.setDeferredPrompt
  );
  const isPromptFired = useInstallPromptStore((state) => state.isPromptFired);

  const handlePwaInstallLink = (pwaLinkData) => {
    const isFirstOpen = useLocalStorageStore.getState().firstOpen !== 'done';
    const linkData = {
      ...pwaLinkData,
      user_status: isFirstOpen ? 'new' : 'returning',
    };
    useLocalStorageStore.getState().setPwaInstallDeeplink(linkData);

    // track the link click for pwa install
    MixpanelEvent('deeplink_clicked', linkData);
    sendPostHogEvent('deeplink_clicked', linkData);

    const { isChrome, isSafari, isFirefox } = getBrowser();

    // check if the user is on PWA mode
    const isStandalone = window.matchMedia(
      '(display-mode: standalone)'
    ).matches;
    const promptFired = window.sessionStorage.getItem('deferredPromptFired');

    // If the user is not on PWA mode, show the install prompt
    if (!isStandalone) {
      if (isChrome && (isPromptFired || promptFired)) {
        setPwaInstall(true);
      } else if (isSafari) {
        setShowSafariModal(true);
      } else if (isFirefox) {
        setShowFirefoxModal(true);
      }
    }
  };

  const checkUTMParams = () => {
    const url = new URL(window.location.href);
    const utmMedium = url.searchParams.get('utm_medium');
    const utmCampaign = url.searchParams.get('utm_campaign');
    const utmSource = url.searchParams.get('utm_source');
    const isPwaInstallLink = utmSource === 'twitter' && utmMedium === 'pwa';

    //remove search params from the url
    url.searchParams.delete('utm_medium');
    url.searchParams.delete('utm_campaign');
    url.searchParams.delete('utm_source');
    window.history.replaceState({}, '', url);

    return { isPwaInstallLink, utmSource, utmMedium, utmCampaign };
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.sessionStorage.setItem('deferredPromptFired', true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
    };
  }, [setDeferredPrompt]);

  useEffect(() => {
    try {
      initMixpanel();
      initPostHog();
      initDataDog();

      if (!window.ReactNativeWebView && !window.isExpoWebView) {
        const { isPwaInstallLink, utmSource, utmMedium, utmCampaign } =
          checkUTMParams();
        if (isPwaInstallLink) {
          handlePwaInstallLink({
            source: utmSource,
            medium: utmMedium,
            campaign: utmCampaign,
          });
        }
      }

      branch.init(process.env.REACT_APP_BRANCH_KEY, (err, data) => {
        if (err) {
          console.warn('Branch initialization failed:', err);
          if (
            err.code === 'NO_CONNECTION' ||
            err.message.includes('Request blocked by client, probably adblock')
          ) {
            if (!sessionStorage.getItem('branch_redirected')) {
              sessionStorage.setItem('branch_redirected', 'true');
              window.location.href = window.location.origin;
            }
          }
          return;
        }

        const linkData = data?.data_parsed;

        if (window.ReactNativeWebView || window.isExpoWebView) {
          return;
        }

        //  const trackDeepLinkInstallFn = async (parsedData) => {
        //     window.ReactNativeWebView?.postMessage(
        //       JSON.stringify({
        //         type: 'LOG',
        //         message: 'Tracking deep link install',
        //       })
        //     );
        //     try {
        //       const source = parsedData['~channel'] ?? '';
        //       const medium = parsedData['~feature'] ?? '';
        //       const campaign = parsedData['~campaign'] ?? '';
        //       const link = parsedData['~referring_link'] ?? '';
        //       const queryParams = getQueryParams(parsedData);
        //       const isPLG = Boolean(queryParams.is_plg_link === 'true');
        //       const isFirstOpen = useLocalStorageStore.getState().firstOpen;

        //       if (isPLG) {
        //         MixpanelEvent(
        //           'User Audience Link Clicked_PLG',
        //           {
        //             medium,
        //             source,
        //             campaign,
        //             link,
        //             user_status: isFirstOpen === null ? 'new' : 'existing',
        //             ...queryParams,
        //           },
        //           true
        //         );
        //       } else {
        //         MixpanelEvent(
        //           'User Audience Link Clicked',
        //           {
        //             medium,
        //             source,
        //             campaign,
        //             link,
        //             user_status: isFirstOpen === null ? 'new' : 'existing',
        //             ...queryParams,
        //           },
        //           true
        //         );
        //       }

        //       if (isFirstOpen === null) {
        //         setFirstOpen('done');

        //         if (isPLG) {
        //           MixpanelEvent(
        //             'Install via Deep Link_PLG',
        //             {
        //               source,
        //               medium,
        //               campaign,
        //               link,
        //               ...queryParams,
        //             },
        //             true
        //           );
        //         } else {
        //           MixpanelEvent(
        //             'Install via Deep Link',
        //             {
        //               source,
        //               medium,
        //               campaign,
        //               link,
        //               ...queryParams,
        //             },
        //             true
        //           );
        //         }
        //       }
        //     } catch (error) {
        //       console.error('Error tracking deep link install:', error);
        //     }
        //   };

        //   trackDeepLinkInstallFn(linkData);

        // PWA Install Link Support using branch link
        if (linkData?.link_type === 'pwa_install') {
          handlePwaInstallLink({
            source: linkData['~channel'],
            medium: linkData['~feature'],
            campaign: linkData['~campaign'],
            link: linkData['~referring_link'],
            ...getQueryParams(linkData),
          });
        }
      });
    } catch (error) {
      console.warn('Error initializing branch:', error);
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    const promptFired = window.sessionStorage.getItem('deferredPromptFired');
    const isStandalone = window.matchMedia(
      '(display-mode: standalone)'
    ).matches;
    const isWebView = window.ReactNativeWebView;

    if ((isPromptFired || promptFired) && !isStandalone && !isWebView) {
      const pwaInstallLinkData =
        useLocalStorageStore.getState().pwaInstallDeeplink;
      if (pwaInstallLinkData && Object.keys(pwaInstallLinkData).length > 0) {
        setPwaInstall(true);
      } else {
        setTimeout(() => {
          setPwaInstall(true);
        }, 5000);
      }
    }
  }, [isPromptFired]);

  const handleInstallClick = () => {
    const { isChrome } = getBrowser();
    const parsedData = useLocalStorageStore.getState().pwaInstallDeeplink || {};

    if (deferredPrompt && isChrome) {
      MixpanelEvent(
        'cta_clicked',
        {
          browser: 'chrome',
          ...parsedData,
          cta_context: 'pwa_install_banner',
          cta_page: window.location.pathname,
          cta_content: 'download',
        },
        true
      );

      sendPostHogEvent(
        'cta_clicked',
        {
          browser: 'chrome',
          ...parsedData,
          cta_context: 'pwa_install_banner',
          cta_page: window.location.pathname,
          cta_content: 'download',
        },
        true
      );
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          setPwaInstall(false);

          MixpanelEvent(
            'download_success',
            {
              browser: 'chrome',
              ...parsedData,
            },
            true
          );
          sendPostHogEvent(
            'download_success',
            {
              browser: 'chrome',
              ...parsedData,
            },
            true
          );
        } else {
          setPwaInstall(false);
          MixpanelEvent(
            'download_dismissed',
            {
              browser: 'chrome',
              ...parsedData,
            },
            true
          );
          sendPostHogEvent(
            'download_dismissed',
            {
              browser: 'chrome',
              ...parsedData,
            },
            true
          );
        }
        clearDeferredPrompt();
        window.sessionStorage.removeItem('deferredPromptFired');
      });
    }
  };

  return (
    <ErrorBoundary
      onError={(error, errorInfo) =>
        logErrorToDataDog(error, errorInfo, { source: 'app' })
      }
      fallback={ErrorFallback}
    >
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <Suspense fallback={<div>Loading App...</div>}>
          <div className='text-poppins-regular relative font-poppins'>
            <App />
            {!window.ReactNativeWebView && (
              <PWAInstallBanner
                isVisible={isPwaInstall}
                onInstall={handleInstallClick}
                onClose={() => {
                  setPwaInstall(false);
                }}
              />
            )}
          </div>
        </Suspense>
      </GoogleOAuthProvider>
      {showSafariModal && (
        <SafariModal
          setShowSafariModal={() => {
            setShowSafariModal(false);
          }}
        />
      )}
      {showFirefoxModal && (
        <FireFoxModal
          setShowFirefoxModal={() => {
            setShowFirefoxModal(false);
          }}
        />
      )}
    </ErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Main data-attr-allow='true' />);

// Register the service worker
// serviceWorkerRegistration.register();
